// Redux Utils
import { useSelector, useDispatch } from "react-redux";

// React Utils
import { useEffect, useState } from "react";
import Breadcrum from "@/components/Breadcrum";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Componentes
import Modal from "@/components/Modals/Modal";
import TransferirTicket from "@/components/TicketsUsuario/CardTicket/TransferirTicket";
import UsuariosParaTransferir from "@/components/TicketsUsuario/CardTicket/UsuariosParaTransferir";
import { useToast, ToastContainer } from "@/components/ui/Toast";

// Package components
import QRCode from "react-qr-code";

// Skeleton
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Utils
import { formatFechaHoraEvento } from "@/lib/utilsUI";
import { parseUTCDate, DEFAULT_TIMEZONE } from "@/utils/dateHelpers";
import { DateTime } from "luxon";

// Thunks
import {
  getDetalleTicketComprado,
  transferirTicket,
} from "@/features/user/userThunks";
import { userReset } from "@/features/user/userSlice";

// Assets
import PLACEHOLDER_IMAGE from "@/assets/avatars/avatar-generic.webp";

export default function TicketsUsuarioId() {
  // Redux
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { ticketDetalle, failureUser, error, errorStatus } = useSelector((state) => state.user);

  // Toast
  const { toasts, success, error: showError, warning, removeToast } = useToast();

  // Obtener el ID del ticket desde los parámetros de la URL
  const { idTicket } = useParams();
  console.log("Rendering TicketsUsuarioId with idTicket:", idTicket);

  // Estados locales
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenTransferir, setIsOpenTransferir] = useState(false);
  const [usuarioATransferir, setUsuarioATransferir] = useState(null);
  const [isOpenModalConfirmar, setIsOpenModalConfirmar] = useState(false);
  const [aceptoIrreversible, setAceptoIrreversible] = useState(false);
  const [isTransfiriendo, setIsTransfiriendo] = useState(false);

  // Cargar información del ticket
  useEffect(() => {
    const loadTicketDetails = async () => {
      setIsLoading(true);
      try {
        await dispatch(getDetalleTicketComprado({ idTicket })).unwrap();
      } catch (error) {
        console.error("Error al cargar detalle del ticket:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (idTicket) {
      loadTicketDetails();
    }
  }, [idTicket, dispatch]);

  // Cleanup: limpiar estados de error al desmontar o cambiar de ticket
  useEffect(() => {
    return () => {
      dispatch(userReset());
    };
  }, [idTicket, dispatch]);

  // Función para obtener configuración de estado
  const getEstadoConfig = (estado) => {
    const configs = {
      0: {
        icon: "fa-clock",
        bgColor: "bg-yellow-500",
        title: "Ticket pendiente de validar",
        subtitle: "No validado",
      },
      1: {
        icon: "fa-check",
        bgColor: "bg-green-600",
        title: "Whoop!",
        subtitle: "Ticket validado",
      },
      2: {
        icon: "fa-times",
        bgColor: "bg-red-600",
        title: "Ticket Rechazado",
        subtitle: "Intento de revalidación",
      },
      3: {
        icon: "fa-calendar-xmark",
        bgColor: "bg-gray-600",
        title: "Ticket Expirado",
        subtitle: "Evento finalizado",
      },
      4: {
        icon: "fa-money-bill-transfer",
        bgColor: "bg-blue-600",
        title: "Reembolsado",
        subtitle: "Evento cancelado",
      },
    };
    return configs[estado] || configs[0];
  };

  // Configuración del breadcrumb
  const linksBreadcrum = [
    {
      label: "Perfil",
      href: `/perfil/${user?.uid}`,
    },
    {
      label: "Detalle Ticket",
      href: `/perfil/${user?.uid}/tickets/${idTicket}`,
    },
  ];

  // Formatear fecha y hora del evento
  const formatearFechaEvento = (fechaHoraInicio, zonaHoraria) => {
    if (!fechaHoraInicio) return { fecha: "", hora: "" };
    // Interpretar UTC con zona horaria del evento, igual que TicketCardPerfilUsuario
    const fechaEventoDT = parseUTCDate(
      fechaHoraInicio,
      zonaHoraria || DEFAULT_TIMEZONE,
    );
    if (!fechaEventoDT) return { fecha: "", hora: "" };
    const fechaFormateada = formatFechaHoraEvento(
      fechaEventoDT.toJSDate(),
      zonaHoraria || DEFAULT_TIMEZONE,
    );
    const [fecha, hora] = fechaFormateada.split(" - ");
    return { fecha, hora };
  };

  const estadoConfig = ticketDetalle?.ticket
    ? getEstadoConfig(ticketDetalle.ticket.estado)
    : getEstadoConfig(0);
  const { fecha: fechaEvento, hora: horaEvento } = ticketDetalle?.evento
    ? formatearFechaEvento(
        ticketDetalle.evento.fechaHoraInicio,
        ticketDetalle.evento.zonaHoraria,
      )
    : { fecha: "", hora: "" };

  // Determinar qué imagen mostrar
  const imagenTicket =
    ticketDetalle?.tipoTicket?.img || ticketDetalle?.evento?.img;

  // Calcular si puede transferir (más de 24 horas antes del evento)
  const calcularHorasHastaEvento = () => {
    if (!ticketDetalle?.evento?.fechaHoraInicio) return 0;
    const fechaEventoDT = parseUTCDate(
      ticketDetalle.evento.fechaHoraInicio,
      ticketDetalle.evento.zonaHoraria || DEFAULT_TIMEZONE,
    );
    if (!fechaEventoDT) return 0;
    const now = DateTime.now();
    const horasHastaEvento = fechaEventoDT.diff(now, "hours").hours;
    return horasHastaEvento;
  };

  const horasHastaEvento = calcularHorasHastaEvento();
  const puedeTransferir =
    ticketDetalle?.ticket?.estado === 0 &&
    horasHastaEvento > 24 &&
    !ticketDetalle?.ticket?.esPorInvitacion;

  // Manejador para abrir modal de búsqueda de usuarios
  const handleAbrirModalBusqueda = () => {
    if (!puedeTransferir) {
      warning(
        "Las transferencias solo están disponibles hasta 24 horas antes del inicio del evento"
      );
      return;
    }
    setIsOpenTransferir(true);
  };

  // Callback cuando se selecciona un usuario para transferir
  const handleUsuarioSeleccionado = (usuario) => {
    console.log("=== Usuario seleccionado ===");
    console.log("Usuario recibido:", usuario);
    setUsuarioATransferir(usuario);
    setIsOpenModalConfirmar(true);
    setIsOpenTransferir(false);
  };

  // Manejador para confirmar la transferencia
  const handleConfirmarTransferencia = async () => {
    // 🔍 LOGS DE DEPURACIÓN
    console.log("=== DEBUG TRANSFERENCIA ===");
    console.log("handleConfirmarTransferencia llamado");
    console.log("aceptoIrreversible:", aceptoIrreversible);
    console.log("usuarioATransferir:", usuarioATransferir);
    console.log("ticketDetalle:", ticketDetalle);
    console.log("ticketDetalle?.ticket:", ticketDetalle?.ticket);
    console.log("🔎 TODAS LAS CLAVES DEL TICKET:", Object.keys(ticketDetalle?.ticket || {}));
    console.log("ticketDetalle?.ticket?.uidTicket:", ticketDetalle?.ticket?.uidTicket);
    console.log("ticketDetalle?.ticket?.uid:", ticketDetalle?.ticket?.uid);
    console.log("ticketDetalle?.ticket?.qr:", ticketDetalle?.ticket?.qr);
    
    // Intentar encontrar el UID correcto
    const uidTicket = ticketDetalle?.ticket?.uidTicket || 
                      ticketDetalle?.ticket?.uid || 
                      ticketDetalle?.ticket?.qr;
    
    console.log("✅ UID a usar:", uidTicket);
    
    if (
      !aceptoIrreversible ||
      !usuarioATransferir ||
      !uidTicket
    ) {
      console.log("❌ Validación falló - valores:", {
        aceptoIrreversible,
        tieneUsuario: !!usuarioATransferir,
        usuarioUid: usuarioATransferir?.uid,
        tieneTicketDetalle: !!ticketDetalle,
        tieneTicket: !!ticketDetalle?.ticket,
        uidTicket
      });
      showError("Faltan datos requeridos para la transferencia. Por favor, verifica que hayas aceptado los términos y seleccionado un usuario.");
      return;
    }

    console.log("✅ Validación exitosa - Iniciando transferencia...");
    setIsTransfiriendo(true);
    try {
      const response = await dispatch(
        transferirTicket({
          uidDestinatario: usuarioATransferir.uid,
          uidTicket: uidTicket,
        }),
      ).unwrap();

      console.log("✅ Transferencia exitosa:", response);

      // Mostrar toast de éxito
      success(
        response?.message || 
        `Ticket transferido exitosamente a ${usuarioATransferir.nombre} ${usuarioATransferir.apellido}`
      );

      // Éxito: cerrar modal de confirmación y mostrar modal de éxito
      setIsOpenModalConfirmar(false);
      setIsOpenModal(true);

      // Resetear estados
      setAceptoIrreversible(false);
      setUsuarioATransferir(null);

      // Opcional: redirigir después de 2 segundos
      setTimeout(() => {
        window.location.href = `/perfil/${user?.uid}`;
      }, 2000);
    } catch (error) {
      console.error("❌ Error al transferir ticket:", error);
      
      // Extraer el mensaje de error desde la respuesta
      const errorMessage = error?.message || 
        error?.data?.message ||
        error ||
        "Error al transferir el ticket. Por favor, intenta nuevamente.";
      
      showError(errorMessage);
    } finally {
      console.log("🏁 Finalizando - isTransfiriendo = false");
      setIsTransfiriendo(false);
    }
  };

  // Manejador para cancelar la transferencia
  const handleCancelarTransferencia = () => {
    setIsOpenModalConfirmar(false);
    setAceptoIrreversible(false);
    setUsuarioATransferir(null);
  };

  // Función para renderizar error 403 (ticket no pertenece al usuario)
  const renderError403 = () => {
    return (
      <div className="min-h-screen bg-aw-bgbluedark flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto flex flex-col items-center text-center">
          <div className="w-24 h-24 rounded-full bg-red-500 flex items-center justify-center mb-6">
            <FontAwesomeIcon
              icon="fa-solid fa-ban"
              className="text-white text-5xl"
            />
          </div>
          
          <h1 className="text-3xl md:text-4xl text-white font-bold mb-4">
            Acceso Denegado
          </h1>
          
          <p className="text-lg text-white mb-2">
            Este ticket no te pertenece
          </p>
          
          <p className="text-white/80 mb-8">
            Por favor, consulta únicamente la información de tus propios tickets.
          </p>
          
          <Link
            to={`/perfil/${user?.uid}`}
            className="bg-aw-green hover:bg-aw-green/90 text-aw-bgbluedark font-bold py-3 px-8 rounded-full transition-colors duration-200 inline-flex items-center gap-2"
          >
            <FontAwesomeIcon icon="fa-solid fa-arrow-left" />
            Volver a Mis Tickets
          </Link>
        </div>
      </div>
    );
  };

  // Si hay error 403, mostrar vista de acceso denegado
  if (!isLoading && failureUser && errorStatus === 403) {
    return renderError403();
  }

  return (
    <>
      {/* ToastContainer para mostrar notificaciones */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      
      <div className="p-4 sm:p-6 lg:p-8 bg-aw-bgbluedark min-h-screen flex flex-col items-center">
        {/* <Breadcrum links={linksBreadcrum} /> */}
        {/* Botón atrás */}
        <div className="absolute top-8 left-8">
          <Link
            to={`/perfil/${user?.uid}`}
            className="text-white text-xl font-bold"
          >
            <FontAwesomeIcon icon="fa-solid fa-chevron-left" />
          </Link>
        </div>

        <div className="w-full max-w-md mx-auto flex flex-col items-center mt-10">
          {/* Card principal */}
          <div className="relative w-full bg-aw-green rounded-2xl pt-12 pb-4 px-4 flex flex-col items-center shadow-xl">
            {/* Icono de estado */}
            <div
              className={`absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 rounded-full flex items-center justify-center border-6 ${
                isLoading ? "bg-gray-400" : estadoConfig.bgColor
              } border-white z-10`}
            >
              {isLoading ? (
                <Skeleton circle width={80} height={80} />
              ) : (
                <FontAwesomeIcon
                  icon={`fa-solid ${estadoConfig.icon}`}
                  className="text-white text-4xl"
                />
              )}
            </div>

            {/* Contenido ticket */}
            <div className="flex flex-col items-center w-full">
              {/* Título y empresa */}
              {isLoading ? (
                <>
                  <Skeleton width={200} height={30} className="mb-2" />
                  <Skeleton width={250} height={20} className="mb-2" />
                  <Skeleton width={180} height={20} className="mb-4" />
                </>
              ) : (
                <>
                  <h2 className="text-lg font-bold text-aw-bluegray text-center mt-2">
                    {/* Título del estado */}
                    {estadoConfig.title}
                  </h2>
                  <div className="text-3xl text-aw-bgbluedark text-center">
                    <span className="text-aw-dark font-bold">
                      {/* Nombre del evento */}
                      {ticketDetalle?.evento?.nombre}
                      {/* TODO: Agregar el nombre de la empresa si el ticket es de cortesía */}
                      {/* @{ticketDetalle?.evento?.nombreEmpresa} */}
                    </span>
                  </div>
                  <div className="text-lg font-bold text-aw-blue text-center mb-2">
                    Tipo:{" "}
                    <span className=" font-bold text-aw-gray">
                      {ticketDetalle?.tipoTicket?.nombre}
                    </span>
                  </div>
                </>
              )}

              {/* Detalles del ticket */}
              <div className="flex w-full text-sm my-2">
                <table className="w-full">
                  <tbody>
                    <tr className="h-6">
                      <td className="w-4/12 font-medium">Fecha</td>
                      <td className="w-8/12 font-semibold text-right">
                        {isLoading ? <Skeleton width={150} /> : fechaEvento}
                      </td>
                    </tr>
                    <tr className="h-6">
                      <td className="w-4/12 font-medium">Hora</td>
                      <td className="w-8/12 font-semibold text-right">
                        {isLoading ? <Skeleton width={120} /> : horaEvento}
                      </td>
                    </tr>
                    <tr className="h-6">
                      <td className="w-4/12 font-medium">Nombre</td>
                      <td className="w-8/12 font-semibold text-right">
                        {isLoading ? (
                          <Skeleton width={150} />
                        ) : (
                          ticketDetalle?.compra?.nombreComprador
                        )}
                      </td>
                    </tr>

                    {/* {ticketDetalle?.ticket?.cantidadPersonas != false && (
                      <tr className="h-6 text-blue-600">
                        <td className="w-4/12 font-semibold">Ticket Grupal:</td>
                        <td className="w-8/12 font-semibold text-right">
                          {isLoading ? (
                            <Skeleton width={150} />
                          ) : (
                            `${ticketDetalle.ticket.cantidadPersonas} personas`
                          )}
                        </td>
                      </tr>
                    )} */}
                    <tr className="h-6">
                      <td className="w-4/12 font-medium">Precio</td>
                      <td className="w-8/12 font-semibold text-right">
                        {isLoading ? (
                          <Skeleton width={100} />
                        ) : (
                          `$${ticketDetalle?.precio?.precioPagado?.toLocaleString("es-CL") || "0"}`
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="flex flex-col w-full text-center mb-2">
                <span className="w-full font-medium text-base">Dirección</span>
                <span className="w-full font-semibold text-sm text-center overflow-hidden">
                  {isLoading ? (
                    <Skeleton width={180} count={2} />
                  ) : (
                    ticketDetalle?.evento?.direccion
                  )}
                </span>
              </div>

              {/* Botones de acción */}
              <div className="flex gap-2 w-full max-w-xs mb-4">
                {isLoading ? (
                  <Skeleton width="100%" height={40} />
                ) : (
                  <button
                    className="flex-1 bg-aw-green font-bold text-aw-bluedark rounded-lg py-2 text-[0.65rem] border-2 border-aw-bgbluedark hover:bg-aw-bluedark hover:text-aw-green transition hover:scale-95 duration-300"
                    onClick={() => {
                      const direccion = encodeURIComponent(
                        ticketDetalle?.evento?.direccion || "",
                      );
                      window.open(
                        `https://www.google.com/maps/search/?api=1&query=${direccion}`,
                        "_blank",
                      );
                    }}
                  >
                    Ir a Google Maps
                  </button>
                )}
              </div>

              <hr className="w-full border-1 border-aw-bgbluedark my-2" />

              {/* Botón transferir */}
              {!isLoading && ticketDetalle?.ticket?.estado === 0 && (
                <div className="flex flex-col items-center w-2/3 gap-2">
                  <button
                    onClick={handleAbrirModalBusqueda}
                    disabled={!puedeTransferir}
                    className={`flex-1 w-full font-bold rounded-lg py-2 text-[0.65rem] border-2 transition duration-300 ${
                      puedeTransferir
                        ? "bg-aw-green text-aw-bluedark border-aw-bgbluedark hover:bg-aw-bluedark hover:text-aw-green hover:scale-95"
                        : "bg-gray-400 text-gray-600 border-gray-500 cursor-not-allowed opacity-50"
                    }`}
                  >
                    Transferir Ticket
                  </button>
                  <p className="text-[10px] text-aw-bluedark-50 text-center">
                    Las transferencias solo están disponibles hasta 24 horas
                    antes del inicio del evento
                  </p>
                </div>
              )}

              {/* Separador decorativo */}
              <div className="w-full relative">
                <div className="absolute -top-2.5 -left-4 w-5 h-10 bg-aw-bgbluedark rounded-r-full"></div>
                <hr
                  className="w-11/12 border-aw-bgbluedark my-2 mx-auto border-dashed"
                  style={{ borderTopWidth: 2 }}
                />
                <div className="absolute -top-2.5 -right-4 w-5 h-10 bg-aw-bgbluedark rounded-l-full"></div>
              </div>

              {/* Imagen y QR */}
              <div className="flex flex-col justify-center items-center w-full max-w-xs gap-2 mt-2 mb-2">
                <div className="w-11/12 flex items-center justify-center bg-white p-2 rounded-lg">
                  {isLoading ? (
                    <Skeleton width="100%" height={144} />
                  ) : (
                    ticketDetalle?.ticket?.qr && (
                      <div className="relative bg-white p-2 rounded-lg w-full">
                        {/* TODO: Agregar personalización de QR */}
                        {/* <div className="absolute w-14 h-14 bg-white top-[40%] left-[42%]">
                          <img
                            src={imagenTicket}
                            alt="Evento"
                            className="rounded-lg w-full h-full object-cover"
                          />
                        </div> */}
                        <QRCode
                          value={ticketDetalle.ticket.qr}
                          size={128}
                          style={{
                            height: "auto",
                            maxWidth: "100%",
                            width: "100%",
                          }}
                        />
                      </div>
                    )
                  )}
                </div>
                {/* Imagen ticket o evento */}
                <div className="w-full flex items-center justify-center">
                  {isLoading ? (
                    <Skeleton width="100%" height={128} />
                  ) : (
                    <img
                      src={imagenTicket}
                      alt="Evento"
                      className="rounded-lg w-full h-auto object-cover"
                    />
                  )}
                </div>
              </div>

              {/* Información adicional */}
              {/* {!isLoading && ticketDetalle?.ticket && (
                <div className="w-full text-xs text-aw-bgbluedark mt-2 space-y-1">
                  {ticketDetalle.ticket.ticketGrupal && (
                    <div className="flex justify-between">
                      <span className="font-medium">Ticket Grupal:</span>
                      <span className="font-semibold">
                        {ticketDetalle.ticket.cantidadPersonas} personas
                      </span>
                    </div>
                  )}
                </div>
              )} */}
            </div>
          </div>

          <div className="w-full text-[10px] text-white text-center mt-2">
            El contenido de cada Ticket es responsabilidad exclusiva de la
            Empresa afiliada. AppWhoop Ticket no se responsabiliza ni interviene
            en valores, horarios, ubicación, descuentos, beneficios o
            información. La Empresa es el único y exclusivo responsable de la
            producción y organización, en forma oportuna y en conformidad a la
            ley.
          </div>
        </div>
      </div>
      <TransferirTicket
        isOpen={isOpenTransferir}
        onClose={() => setIsOpenTransferir(false)}
        large="max-w-4xl"
      >
        <UsuariosParaTransferir
          closeTransferir={() => setIsOpenTransferir(false)}
          onTransferirClick={handleUsuarioSeleccionado}
        />
      </TransferirTicket>

      {/* Modal de confirmación de transferencia */}
      <Modal
        isOpen={isOpenModalConfirmar}
        onClose={handleCancelarTransferencia}
        large="max-w-lg"
      >
        <div className="p-6 bg-aw-bluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green">
          <div className="text-center mb-6">
            <FontAwesomeIcon
              icon={"fa-solid fa-triangle-exclamation"}
              className="text-yellow-500 text-6xl mb-4 mx-auto"
            />
            <h2 className="text-xl sm:text-2xl font-semibold mb-2 text-white">
              Confirmación de Transferencia
            </h2>
            <p className="text-gray-400 mb-1 text-sm">
              Estás por transferir este ticket a:
            </p>
            <div className="flex flex-row items-center justify-center gap-2 mb-1">
              <img
                src={usuarioATransferir?.imgPerfil || PLACEHOLDER_IMAGE}
                alt="Ticket"
                className="rounded-full w-20 h-20 border-2 border-aw-green object-cover"
              />
              <div className="text-left text-sm">
                <span className="font-bold text-white block">
                  {usuarioATransferir?.nombre} {usuarioATransferir?.apellido}
                </span>
                {usuarioATransferir?.alias && (
                  <span className="text-aw-green block">
                    {" "}
                    (@{usuarioATransferir.alias})
                  </span>
                )}
              </div>
            </div>
            <p className="text-gray-400 mb-1 text-sm">
              Esta acción es{" "}
              <span className="font-bold text-red-500">IRREVERSIBLE</span> y no
              podrá deshacerse.
            </p>
            <p className="text-yellow-500 text-xs">
              <b className="underline">Nota:</b> Recuerda que nuestras{" "}
              <a
                href="/politica-de-privacidad-y-terminos-y-condiciones"
                className="text-aw-green underline"
              >
                Póliticas
              </a>{" "}
              prohíben la reventa y actividades ilegales. Transfiere el ticket
              solo a amigos o familiares de confianza.
            </p>
          </div>

          {/* Checkbox de confirmación */}
          <div className="flex items-start gap-3 mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <input
              type="checkbox"
              id="aceptoIrreversible"
              checked={aceptoIrreversible}
              onChange={(e) => {
                console.log("Checkbox cambiado:", e.target.checked);
                setAceptoIrreversible(e.target.checked);
              }}
              className="mt-1 w-5 h-5 text-aw-green focus:ring-aw-green rounded cursor-pointer"
            />
            <label
              htmlFor="aceptoIrreversible"
              className="text-xs text-aw-bluedark cursor-pointer select-none"
            >
              Entiendo que la transferencia es irreversible y permanente. Y que
              no participo en reventa ni actividades ilegales.
            </label>
          </div>

          {/* Botones de acción */}
          <div className="flex gap-3">
            <button
              onClick={handleCancelarTransferencia}
              disabled={isTransfiriendo}
              className="flex-1 text-sm bg-gray-300 text-aw-bluedark font-semibold rounded-lg py-3 hover:bg-gray-400 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                console.log("=== BOTÓN CONFIRMAR CLICKEADO ===");
                console.log("disabled:", !aceptoIrreversible || isTransfiriendo);
                console.log("aceptoIrreversible:", aceptoIrreversible);
                console.log("isTransfiriendo:", isTransfiriendo);
                handleConfirmarTransferencia();
              }}
              disabled={!aceptoIrreversible || isTransfiriendo}
              className="flex-1 text-sm bg-aw-green text-aw-bluedark font-semibold rounded-lg py-3 hover:bg-aw-green-70 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isTransfiriendo ? (
                <>
                  <FontAwesomeIcon
                    icon="fa-solid fa-spinner"
                    className="animate-spin"
                  />
                  Transfiriendo...
                </>
              ) : (
                "Confirmar Transferencia"
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de éxito */}
      <Modal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        large="max-w-4xl"
      >
        <div className="p-6 text-center bg-aw-bluedark rounded-lg">
          <div className="text-center">
            <FontAwesomeIcon
              icon={"fa-solid fa-circle-check"}
              className="text-aw-green text-6xl mb-4 mx-auto"
            />
            <h2 className="text-2xl font-semibold mb-2 text-white">
              Transferencia Exitosa!
            </h2>
            <p className="text-gray-400 mb-4">
              Pronto le llegará una notificación a tu amigo!
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
}
